/* eslint-disable no-console */
import axios from 'axios';
import {compare} from 'fast-json-patch';
import {HOST} from '../constants';
import {IRunFile} from '../incase-domain/proptypes/runfile';
import {IPresentation} from '../incase-domain/presentation';
import {ISlide} from '../incase-domain/slide';
import {ProtectionInfo} from '../incase-domain/presentationProtection';
import {RequestParam} from '../incase-domain/requestParams';
import {httpPut} from './http.service';

export interface SlugResolution {
    presentationId: string;
    params: {key: string; value: string}[];
}

export const resolvePresentationSlug = (slug: string): Promise<SlugResolution | null> => {
    return axios
        .get(`${HOST}/player/presentation/slug/${slug}`)
        .then((res) => res.data as SlugResolution)
        .catch(() => null);
};

export const getPresentationProtection = (presId: string): Promise<ProtectionInfo> => {
    return axios
        .get(`${HOST}/player/presentation/protection/${presId}`)
        .then((res) => res.data)
        .catch((error) => {
            console.log(error);
        });
};

export const getPresentationRequestParams = (presId: string): Promise<RequestParam[] | null> => {
    return axios
        .get(`${HOST}/player/presentation/request-params/${presId}`)
        .then((res) => res.data || [])
        .catch((error) => {
            console.log(error);
            return null;
        });
};

export const isPresentationOwner = (presId: string): Promise<boolean> => {
    return axios
        .get(`${HOST}/player/presentation/owner/${presId}`)
        .then((res) => res.data.isOwner)
        .catch((error) => {
            console.log(error);
            return false;
        });
};

export const fetchPresentationById = (
    presentationId: string,
    credential: string | null,
    params: Record<string, string>
): Promise<IPresentation> => {
    const authorization = credential;

    return axios.get(`${HOST}/player/presentation/${presentationId}`, {params, headers: {authorization}}).then((response) => {
        return response.data;
    });
};

export const fetchPresentationForSlidePreview = (
    presentationId: string,
    slideId: string,
    exp: string,
    sig: string
): Promise<IPresentation> => {
    return axios
        .get(`${HOST}/player/presentation/slide-preview/${presentationId}`, {
            params: {slideId, exp, sig},
        })
        .then((response) => response.data);
};

export const fetchPresentationSlides = (
    presentationId: string,
    size: number,
    page: number,
    credential: string | null,
    loadingState: (state: number) => void
): Promise<ISlide[]> => {
    const authorization = credential;

    return axios
        .get(`${HOST}/player/presentation/${presentationId}/slides/${size}/${page}`, {
            headers: {authorization},
            onDownloadProgress: (e) => {
                if (e.total !== undefined) {
                    loadingState((e.loaded * 100) / e.total);
                }
            },
        })
        .then((response) => {
            return response.data.slides;
        });
};

export const fetchPresentationRunFile = (runFileId: string): Promise<IRunFile> => {
    return axios
        .get(`${HOST}/player/getRunFile/${runFileId}`)
        .then((response) => {
            return response.data;
        })
        .catch((error) => {
            console.log(error);
        });
};

export const createPresentationRunFile = (
    presentationId: string,
    requestParams?: Record<string, string>,
    roomId?: string,
    roomRunfileId?: string,
    guestIdentification?: string
): Promise<IRunFile> => {
    return httpPut(`player/createRunFile`, {
        presentationId,
        requestParams,
        roomId,
        roomRunfileId,
        guestIdentification,
    }).catch(() => {
        return {
            slideDrawLines: [],
            elementInteractions: {},
            chalkboardUserComments: [],
            timeElapsed: 0,
        };
    });
};

export async function patchPresentationRunFile(oldRunFile: IRunFile, newRunFile: IRunFile): Promise<{failed: boolean} | IRunFile> {
    if (!newRunFile._id || !oldRunFile._id) {
        console.error('Both old and new RunFiles must have an _id for patching');
        return {failed: true};
    }
    if (newRunFile._id !== oldRunFile._id) {
        console.error('RunFile IDs do not match for patching');
        return {failed: true};
    }
    const patch = compare(oldRunFile, newRunFile);

    try {
        await axios.patch(`${HOST}/player/patchRunFile`, {id: newRunFile._id, patch});
        return {failed: false};
    } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 409) {
            try {
                const freshRunFile = await fetchPresentationRunFile(newRunFile._id);
                const retryPatch = compare(freshRunFile, newRunFile);
                await axios.patch(`${HOST}/player/patchRunFile`, {id: newRunFile._id, patch: retryPatch});
                return {failed: false};
            } catch {
                return {failed: true};
            }
        }
        return {failed: true};
    }
}
